import * as React from "react"

import { CatalogNavigation } from "shared/hubrise/catalogNavigation"
import { LastVisits } from "shared/hubrise/catalogNavigation/LastVisits"
import { Editor } from "shared/hubrise/editor"
import { ICatalog } from "shared/hubrise/models"

type TCatalogNavigationDictionary = { [catalogId: string]: CatalogNavigation }

interface NavigationContextState {
  // This is stored in a ref to avoid re-rendering consumers on every page change. So it is important that all mutable
  // information is returned by functions/methods, and not stored directly in the state.
  catalogNavigations: TCatalogNavigationDictionary
}

type NavigationContextInterface = {
  getCatalogNavigation: (catalogId: string) => CatalogNavigation | undefined
  getOrCreateCatalogNavigation: (catalog: ICatalog) => CatalogNavigation
  visit: (editor: Editor, ...visitArgs: Parameters<LastVisits["visit"]>) => void
}

const NavigationContext = React.createContext<NavigationContextInterface | undefined>(undefined)

interface NavigationContextProviderProps {
  children: React.ReactNode
}

export const NavigationContextProvider = ({ children }: NavigationContextProviderProps): JSX.Element => {
  // useRef to avoid re-rendering consumers on every page change.
  const state = React.useRef<NavigationContextState>({ catalogNavigations: {} })

  const getCatalogNavigation = React.useCallback<NavigationContextInterface["getCatalogNavigation"]>((catalogId) => {
    return state.current.catalogNavigations[catalogId]
  }, [])

  const getOrCreateCatalogNavigation = React.useCallback<NavigationContextInterface["getOrCreateCatalogNavigation"]>(
    (catalog) => {
      let catalogNavigation = state.current.catalogNavigations[catalog.hrId]
      if (catalogNavigation === undefined) {
        catalogNavigation = new CatalogNavigation(catalog)
        state.current = {
          catalogNavigations: { ...state.current.catalogNavigations, [catalog.hrId]: catalogNavigation },
        }
      }
      return catalogNavigation
    },
    [],
  )

  const visit = React.useCallback<NavigationContextInterface["visit"]>(
    (editor, ...visitArgs) => {
      const catalogNavigation = getOrCreateCatalogNavigation(editor.catalog)
      catalogNavigation.visit(...visitArgs)
    },
    [getOrCreateCatalogNavigation],
  )

  const value = React.useMemo(
    () => ({ getCatalogNavigation, getOrCreateCatalogNavigation, visit }),
    [getCatalogNavigation, getOrCreateCatalogNavigation, visit],
  )
  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>
}

export function useNavigationContext(): NavigationContextInterface {
  const context = React.useContext(NavigationContext)

  if (context === undefined) {
    throw new Error(`useNavigationContext must be used within NavigationContextProvider`)
  }

  return context
}
